import { firebaseBattleService } from './firebaseBattle';
import { userAPI, typingTestAPI, leaderboardAPI, paymentAPI } from './apiClient';
import { User } from '../types';
import { TextType, PremiumStatus } from '../types/database';

// DatabaseService manages access to both MongoDB (via API) and Firebase services
export class DatabaseService {
  private static instance: DatabaseService;
  
  private constructor() {}
  
  public static getInstance(): DatabaseService {
    if (!DatabaseService.instance) {
      DatabaseService.instance = new DatabaseService();
    }
    return DatabaseService.instance;
  }
  
  // Initialize both services with the user ID
  public async init(userId: string): Promise<void> {
    try {
      await firebaseBattleService.init(userId);
    } catch (error) {
      console.warn('Firebase battle service not initialized:', error);
    }
  }
  
  // User Methods (from MongoDB via API)
  public async getUser(userId: string): Promise<User | null> {
    try {
      const user = await userAPI.getById(userId);
      return this.transformMongoUser(user);
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  }
  
  public async getUserByEmail(email: string): Promise<User | null> {
    try {
      const user = await userAPI.getByEmail(email);
      return this.transformMongoUser(user);
    } catch (error) {
      console.error('Error fetching user by email:', error);
      return null;
    }
  }
  
  public async getUserByEmailWithPassword(email: string): Promise<any> {
    try {
      return await userAPI.getByEmail(email);
    } catch (error) {
      console.error('Error fetching user with password:', error);
      return null;
    }
  }
  
  public async createUser(userData: Partial<User>): Promise<User | null> {
    try {
      const user = await userAPI.create(userData);
      
      if (user?._id) {
        await this.init(user._id.toString());
      }
      
      return this.transformMongoUser(user);
    } catch (error) {
      console.error('Error creating user:', error);
      return null;
    }
  }
  
  public async updateUser(userId: string, userData: Partial<User>): Promise<User | null> {
    try {
      const user = await userAPI.update(userId, userData);
      return this.transformMongoUser(user);
    } catch (error) {
      console.error('Error updating user:', error);
      return null;
    }
  }
  
  // Premium subscription methods
  public async updatePremiumStatus(
    userId: string, 
    status: PremiumStatus, 
    expiresAt?: Date
  ): Promise<boolean> {
    try {
      const result = await userAPI.updatePremium(userId, status, expiresAt);
      return result.success;
    } catch (error) {
      console.error('Error updating premium status:', error);
      return false;
    }
  }
  
  // Payment methods
  public async createPayment(
    userId: string,
    amount: number,
    plan: 'monthly' | 'yearly' | 'lifetime',
    paymentId: string,
    gatewayProvider?: string
  ): Promise<boolean> {
    try {
      await paymentAPI.create({
        userId,
        amount,
        plan,
        status: 'pending',
        paymentId,
        gatewayProvider: gatewayProvider || 'atlos',
      });
      return true;
    } catch (error) {
      console.error('Error creating payment:', error);
      return false;
    }
  }
  
  public async completePayment(
    paymentId: string,
    transactionHash: string
  ): Promise<boolean> {
    try {
      const result = await paymentAPI.updateStatus(paymentId, 'completed', transactionHash);
      return result.success;
    } catch (error) {
      console.error('Error completing payment:', error);
      return false;
    }
  }
  
  // Typing test methods
  public async saveTypingTest(testData: {
    userId: string;
    wpm: number;
    accuracy: number;
    rawWpm: number;
    correctChars: number;
    incorrectChars: number;
    totalChars: number;
    timeElapsed: number;
    consistency: number;
    textType: TextType;
  }): Promise<boolean> {
    try {
      await typingTestAPI.create(testData);
      return true;
    } catch (error) {
      console.error('Error saving typing test:', error);
      return false;
    }
  }
  
  public async getUserTypingTests(userId: string, limit: number = 10): Promise<any[]> {
    try {
      return await typingTestAPI.getUserTests(userId, limit);
    } catch (error) {
      console.error('Error fetching typing tests:', error);
      return [];
    }
  }
  
  // Leaderboard methods
  public async updateLeaderboard(
    userId: string,
    username: string,
    country: string,
    wpm: number,
    accuracy: number
  ): Promise<boolean> {
    try {
      await leaderboardAPI.update({
        userId,
        username,
        country,
        wpm,
        accuracy,
      });
      return true;
    } catch (error) {
      console.error('Error updating leaderboard:', error);
      return false;
    }
  }
  
  public async getLeaderboard(category: string = 'wpm') {
    try {
      return await leaderboardAPI.get(category);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      return [];
    }
  }
  
  public async getLeaderboardByCountry(
    country: string,
    category: string = 'wpm'
  ) {
    try {
      return await leaderboardAPI.getByCountry(country, category);
    } catch (error) {
      console.error('Error fetching country leaderboard:', error);
      return [];
    }
  }
  
  // Helper method to transform MongoDB user to app User type
  private transformMongoUser(mongoUser: any): User | null {
    if (!mongoUser) return null;
    
    return {
      id: mongoUser._id?.toString() || mongoUser.id,
      username: mongoUser.username,
      email: mongoUser.email,
      level: mongoUser.level,
      xp: mongoUser.xp,
      totalXp: mongoUser.totalXp,
      isPremium: mongoUser.isPremium,
      premiumExpiresAt: mongoUser.premiumExpiresAt,
      country: mongoUser.country,
      preferences: mongoUser.preferences,
      stats: mongoUser.stats,
      achievements: mongoUser.achievements || [],
      recentActivity: mongoUser.recentActivity || [],
      createdAt: mongoUser.createdAt,
      updatedAt: mongoUser.updatedAt,
    };
  }
  
  // Battle methods (from Firebase)
  
  // All methods are directly passed through to the Firebase service
  public get battle() {
    return firebaseBattleService;
  }
}

// Export a singleton instance
export const dbService = DatabaseService.getInstance();
